import { RtcLink, type RtcOptions } from '../webrtc/rtcLink';
import { RelayLink } from '../relayLink';
import type { Link } from '../link';
import { currentPass } from '../gate';

/** How long a newcomer waits for a direct connection before relaying through the server instead. */
export const DIRECT_TIMEOUT_MS = 8000;

/**
 * WebSocket signalling client for the optional local signalling service
 * (tools/signalling-server.mjs). The server only relays session descriptions
 * and ICE candidates between peers in a room; gameplay never flows through it.
 * Newer peers initiate connections to every existing peer (full mesh). If a direct connection has
 * not opened within DIRECT_TIMEOUT_MS, or fails, the newcomer switches that peer to a RelayLink
 * through the same WebSocket, and the other side follows when the first relayed message arrives.
 */
export class WsSignalling {
  private ws: WebSocket;
  private links = new Map<string, RtcLink>();
  private relays = new Map<string, RelayLink>();
  private earlyIce = new Map<string, RTCIceCandidateInit[]>();
  private released = false;
  onLink: ((l: Link) => void) | null = null;
  /** A peer is being reached through the server rather than directly. */
  onRelay: ((id: string) => void) | null = null;
  onError: ((e: string) => void) | null = null;
  onPeers: ((ids: string[]) => void) | null = null;

  /** `create` marks the host: the online lobby service refuses joiners to a room with no host. */
  constructor(url: string, readonly room: string, readonly localId: string, private opts: RtcOptions, create = false) {
    // Carry the start-up pass; the server refuses connections without one.
    this.ws = new WebSocket(url + (url.includes('?') ? '&' : '?') + 'p=' + encodeURIComponent(currentPass() ?? ''));
    this.ws.onopen = () => this.ws.send(JSON.stringify({ type: 'join', room, id: localId, create }));
    this.ws.onerror = () => this.onError?.(`Could not reach the signalling service at ${url}`);
    this.ws.onmessage = (e) => void this.handle(JSON.parse(String(e.data)));
    this.ws.onclose = () => {
      for (const r of [...this.relays.values()]) r.close();
    };
  }

  /** Whether any peer is reached through the server, so the WebSocket must stay open. */
  get relaying(): boolean {
    return this.relays.size > 0;
  }

  /** Signalling is no longer needed: close now, or once the last relayed peer has gone. */
  release(): void {
    this.released = true;
    if (!this.relaying) this.close();
  }

  private send(m: unknown): void {
    if (this.ws.readyState === WebSocket.OPEN) this.ws.send(JSON.stringify(m));
  }

  private makeLink(remote: string, initiator: boolean): RtcLink {
    const l = new RtcLink(remote, this.opts, initiator);
    l.onIce = (c) => this.send({ type: 'signal', room: this.room, from: this.localId, to: remote, ice: c });
    this.links.set(remote, l);
    void l.opened.then(() => {
      if (this.links.get(remote) === l) this.onLink?.(l);
    });
    if (initiator) {
      // Until the session takes the link over, a failure means: try the relay instead.
      l.onClose = () => this.useRelay(remote, true);
      setTimeout(() => {
        if (!l.open) this.useRelay(remote, true);
      }, DIRECT_TIMEOUT_MS);
    }
    return l;
  }

  /** Reach `remote` through the server from now on; `announce` tells the other side to do the same. */
  private useRelay(remote: string, announce: boolean): RelayLink | null {
    const have = this.relays.get(remote);
    if (have) return have;
    if (this.ws.readyState !== WebSocket.OPEN) return null;
    const rtc = this.links.get(remote);
    if (announce && rtc?.open) return null;
    this.links.delete(remote);
    if (rtc) {
      rtc.onClose = null;
      rtc.onMessage = null;
      rtc.close();
    }
    const r = new RelayLink(
      remote,
      (data) => this.send({ type: 'relay', room: this.room, to: remote, data }),
      () => {
        this.relays.delete(remote);
        if (this.released && !this.relaying) this.close();
      },
    );
    this.relays.set(remote, r);
    if (announce) this.send({ type: 'relay', room: this.room, to: remote, data: '' });
    this.onRelay?.(remote);
    this.onLink?.(r);
    return r;
  }

  private async handle(m: { type: string; peers?: string[]; from?: string; id?: string; data?: string; sdp?: RTCSessionDescriptionInit; ice?: RTCIceCandidateInit; reason?: string }): Promise<void> {
    if (m.type === 'relay' && m.from) {
      const r = this.useRelay(m.from, false);
      if (r && m.data) r.receive(m.data);
    } else if (m.type === 'left' && m.id) {
      this.relays.get(m.id)?.close();
    } else if (m.type === 'error') {
      this.onError?.(m.reason ?? 'The signalling service refused the connection.');
    } else if (m.type === 'peers' && m.peers) {
      this.onPeers?.(m.peers);
      // We are the newcomer: initiate to everyone already present.
      for (const p of m.peers) {
        if (p === this.localId || this.links.has(p)) continue;
        const l = this.makeLink(p, true);
        const offer = await l.createOffer();
        this.send({ type: 'signal', room: this.room, from: this.localId, to: p, sdp: offer });
      }
    } else if (m.type === 'signal' && m.from) {
      if (this.relays.has(m.from)) return;
      let l = this.links.get(m.from);
      if (m.sdp?.type === 'offer') {
        const early = this.earlyIce.get(m.from) ?? [];
        this.earlyIce.delete(m.from);
        l = this.makeLink(m.from, false);
        for (const c of early) void l.addIce(c);
        const ans = await l.acceptOffer(m.sdp);
        this.send({ type: 'signal', room: this.room, from: this.localId, to: m.from, sdp: ans });
      } else if (m.sdp?.type === 'answer' && l) await l.acceptAnswer(m.sdp);
      else if (m.ice && l) await l.addIce(m.ice);
      else if (m.ice) (this.earlyIce.get(m.from) ?? this.earlyIce.set(m.from, []).get(m.from)!).push(m.ice);
    }
  }

  close(): void {
    try {
      this.ws.close();
    } catch {
      /* ignore */
    }
  }
}
